// Ocean.tsx — A calm sea shell rendered ONLY over water. It uses the terrain's
// sea-level geometry (terrain.ts `oceanGeometry`), which carries the per-vertex
// elevation, so the shader discards every land fragment with the SAME fBm-warped
// shoreline the surface shader uses — the water never bleeds onto land. Keeps the
// Fresnel opacity, view-angle depth tint, Blinn-Phong sun glint, and the shared
// analytic eclipse term. Object-space, toneMapped:false.
import { useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { Color, DoubleSide, Vector3 } from 'three';
import type { BufferGeometry, Mesh } from 'three';
import { SHADOW_PARS, makeShadowUniforms, setOccluders } from './shadow';
import type { Occluder } from './shadow';
import { FBM3D, SIMPLEX3D } from './planet/glslNoise';
import { CLIMATE_PARS } from './planet/climateGlsl';
import type { SurfaceClimate } from './planet/surfaceShader';

const vert = /* glsl */ `
  attribute float aElevation;
  varying vec3 vWorldPos;
  varying vec3 vNormal;
  varying vec3 vObjDir;
  varying float vElev;

  void main() {
    vElev = aElevation;
    vObjDir = normalize(position);
    vec4 worldPos = modelMatrix * vec4(position, 1.0);
    vWorldPos = worldPos.xyz;
    vNormal = normalize(mat3(modelMatrix) * normal);
    gl_Position = projectionMatrix * viewMatrix * worldPos;
  }
`;

const frag = /* glsl */ `
  precision highp float;

  uniform vec3  uSunPos;
  uniform vec3  uCameraPos;
  uniform vec3  uShallowColor;
  uniform vec3  uDeepColor;
  uniform float uSeaLevel;
  uniform float uFresnelPower;
  uniform float uFresnelBias;
  uniform float uSpecularPower;
  uniform float uSpecularStrength;
  uniform float uCanFrost;
  uniform float uCanScorch;

  varying vec3 vWorldPos;
  varying vec3 vNormal;
  varying vec3 vObjDir;
  varying float vElev;

  ${SHADOW_PARS}
  ${SIMPLEX3D}
  ${FBM3D}
  ${CLIMATE_PARS}

  void main() {
    // Land mask: same warped sea-level threshold the surface shader classifies on,
    // so the shoreline matches exactly and the sea shell only ever covers water.
    vec3 sdir = normalize(vObjDir);
    float warp = fbm(sdir * 4.0 + 11.0) - 0.5;
    if (vElev + warp * 0.08 >= uSeaLevel) {
      discard;
    }

    vec3 N = normalize(vNormal);
    vec3 V = normalize(uCameraPos - vWorldPos);
    vec3 L = normalize(uSunPos - vWorldPos);

    // Climate gate: liquid sea exists only where it is neither baked off (scorched
    // substellar point / hot desert) nor frozen (cold night face / poles). The surface
    // shader paints the seabed dry-evaporite / sea-ice over the same cells, so discarding
    // the glinting water shell here is what keeps a tidally-locked world's hot face dry
    // instead of an ocean — the water retreats to the temperate terminator ring.
    float seaTempC = climateTempC(sdir.y, dot(N, L));
    float liquid = clamp((1.0 - uCanScorch * climateBake(seaTempC)) *
                         (1.0 - uCanFrost * climateFrost(seaTempC)), 0.0, 1.0);
    if (liquid < 0.03) discard;

    float fres = pow(1.0 - max(dot(N, V), 0.0), uFresnelPower);
    float opacity = clamp(uFresnelBias + (1.0 - uFresnelBias) * fres, 0.0, 1.0);

    float depthAmt = smoothstep(0.0, 1.0, 1.0 - max(dot(N, V), 0.0));
    vec3 water = mix(uShallowColor, uDeepColor, clamp(depthAmt, 0.0, 1.0));

    vec3 Hh = normalize(L + V);
    float spec = pow(max(dot(N, Hh), 0.0), uSpecularPower) * uSpecularStrength;
    spec *= smoothstep(-0.1, 0.2, dot(N, L));

    float diff = clamp(dot(N, L) * 0.5 + 0.5, 0.0, 1.0);

    // A moon's umbra crossing the sea dims the sunlit sheen (the ambient base and
    // grazing fresnel sky-glow remain so it never goes pure black).
    float ecl = sunVisibility(vWorldPos);
    // Warm, tight sun glint (a real specular reflection of the star, not a white
    // plastic blob) at the sub-solar point.
    vec3 sunGlint = vec3(1.0, 0.93, 0.82) * spec * ecl;
    vec3 color = water * (0.35 + 0.65 * diff * ecl) + sunGlint;
    color += fres * 0.15 * vec3(0.6, 0.75, 0.9);

    // The base sheet is mostly transparent (fresnel) so the deep-water surface shows
    // through; let the tight glint lift its own opacity a touch so the glitter reads.
    // Fade by the liquid factor so the water recedes with a soft shoreline as it bakes
    // or freezes off, rather than ending at a hard discard edge.
    float a = clamp((opacity + spec * 0.4 * ecl) * liquid, 0.0, 1.0);
    gl_FragColor = vec4(color, a);
  }
`;

interface Props {
  /** Sea-level mesh carrying aElevation (terrain.ts `oceanGeometry`). */
  geometry: BufferGeometry;
  /** Normalized elevation below which a fragment is sea (preset ramp seaLevel01). */
  seaLevel: number;
  /** World position of the star this planet orbits (== system.position). */
  sunWorldPos: Vector3;
  /** Star core radius (penumbra size for the analytic eclipse). */
  sunRadius: number;
  /** The planet's moons — they eclipse the sea as they transit. */
  occluders: Occluder[];
  /** Per-fragment climate (day/night/mean temps, tilt, frost/scorch gates). The water
   *  shell is discarded where the sea is scorched off or frozen, so it never sits on a
   *  tidally-locked world's hot substellar face. */
  climate: SurfaceClimate;
  /** 1 for a tidally-locked world (selects the substellar temperature field). */
  tidalLock: number;
  shallowColor?: string;
  deepColor?: string;
}

export function Ocean({
  geometry,
  seaLevel,
  sunWorldPos,
  sunRadius,
  occluders,
  climate,
  tidalLock,
  shallowColor = '#0d9b8c',
  deepColor = '#021238',
}: Props) {
  const meshRef = useRef<Mesh>(null);
  const camera = useThree((state) => state.camera);
  const shadow = useMemo(makeShadowUniforms, []);

  const uniforms = useMemo(
    () => ({
      uSunPos: { value: new Vector3() },
      uCameraPos: { value: new Vector3() },
      uShallowColor: { value: new Color(shallowColor) },
      uDeepColor: { value: new Color(deepColor) },
      uSeaLevel: { value: seaLevel },
      uFresnelPower: { value: 5.0 },
      uFresnelBias: { value: 0.08 },
      uSpecularPower: { value: 380.0 },
      uSpecularStrength: { value: 1.4 },
      uMeanT: { value: climate.meanTempC },
      uDayT: { value: climate.dayTempC },
      uNightT: { value: climate.nightTempC },
      uObliq: { value: climate.obliquity },
      uTidalLock: { value: tidalLock },
      uCanFrost: { value: climate.canFrost ? 1 : 0 },
      uCanScorch: { value: climate.canScorch ? 1 : 0 },
      ...shadow,
    }),
    [shallowColor, deepColor, seaLevel, climate, tidalLock, shadow],
  );

  useFrame(() => {
    if (!meshRef.current) {
      return;
    }
    uniforms.uSunPos.value.copy(sunWorldPos);
    uniforms.uCameraPos.value.copy(camera.position);
    setOccluders(shadow, sunWorldPos, sunRadius, occluders);
  });

  return (
    <mesh ref={meshRef} geometry={geometry} renderOrder={1}>
      <shaderMaterial
        vertexShader={vert}
        fragmentShader={frag}
        uniforms={uniforms}
        transparent
        depthWrite={false}
        side={DoubleSide}
        toneMapped={false}
      />
    </mesh>
  );
}
